import { Tabs } from '@base-ui/react/tabs';
import { File, ListX, MoreHorizontal, X } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip } from '@/components/ui/tooltip';
import { springCritical } from '@/lib/motion-config';
import { cn } from '@/lib/utils';

import type { Participant } from '@shared/types';

export interface FileTab {
	path: string;
	hasUnsavedChanges?: boolean;
	isSaving?: boolean;
	label?: string;
}

export interface FileTabsProperties {
	tabs: FileTab[];
	activeTab: string | undefined;
	onSelect: (path: string) => void;
	onClose: (path: string) => void;
	participants?: Participant[];
	onCloseAll?: () => void;
	actions?: React.ReactNode;
	className?: string;
}

function getFilename(path: string): string {
	const parts = path.split('/');
	return parts.at(-1) || path;
}

function getParentDirectory(path: string): string {
	const parts = path.split('/').filter(Boolean);
	return parts.length > 1 ? (parts.at(-2) ?? '') : '';
}

function getFileIconColor(path: string): string {
	const extension = path.split('.').pop()?.toLowerCase();
	switch (extension) {
		case 'ts':
		case 'tsx': {
			return 'text-blue-600 dark:text-blue-400';
		}
		case 'js':
		case 'jsx': {
			return 'text-yellow-600 dark:text-yellow-400';
		}
		case 'css': {
			return 'text-purple-600 dark:text-purple-400';
		}
		case 'html': {
			return 'text-orange-600 dark:text-orange-400';
		}
		case 'json': {
			return 'text-green-600 dark:text-green-400';
		}
		case 'md': {
			return 'text-gray-400';
		}
		default: {
			return 'text-text-secondary';
		}
	}
}

/**
 * Build a map of filenames that appear multiple times across open tabs.
 * Returns a set of paths that need disambiguation (parent directory shown).
 */
function getDuplicateBasenames(tabs: FileTab[]): Set<string> {
	const basenameCount = new Map<string, string[]>();
	for (const tab of tabs) {
		const basename = getFilename(tab.path);
		const existing = basenameCount.get(basename) ?? [];
		existing.push(tab.path);
		basenameCount.set(basename, existing);
	}
	const duplicates = new Set<string>();
	for (const paths of basenameCount.values()) {
		if (paths.length > 1) {
			for (const path of paths) {
				duplicates.add(path);
			}
		}
	}
	return duplicates;
}

function handleClosePointerDown(event: React.PointerEvent) {
	event.stopPropagation();
}

export function FileTabs({ tabs, activeTab, onSelect, onClose, onCloseAll, actions, participants = [], className }: FileTabsProperties) {
	const duplicates = useMemo(() => getDuplicateBasenames(tabs), [tabs]);
	const listReference = useRef<HTMLDivElement>(null);

	// Scroll the active tab into view when it changes
	useEffect(() => {
		if (!activeTab || !listReference.current) return;
		const activeElement = listReference.current.querySelector<HTMLElement>(`[data-active]`);
		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
		}
	}, [activeTab]);

	// Allow horizontal scrolling with the mouse wheel
	const handleWheel = useCallback((event: React.WheelEvent) => {
		if (listReference.current && event.deltaY !== 0) {
			listReference.current.scrollLeft += event.deltaY;
		}
	}, []);

	// Pointer drag-to-scroll (mouse + touch, like VS Code)
	const dragState = useRef<{ isDown: boolean; startX: number; scrollLeft: number }>({
		isDown: false,
		startX: 0,
		scrollLeft: 0,
	});

	const handlePointerDown = useCallback((event: React.PointerEvent) => {
		const list = listReference.current;
		if (!list) return;
		// Only drag on primary button (left click / single touch)
		if (event.button !== 0) return;
		dragState.current = { isDown: true, startX: event.clientX, scrollLeft: list.scrollLeft };
		list.setPointerCapture(event.pointerId);
	}, []);

	const handlePointerMove = useCallback((event: React.PointerEvent) => {
		if (!dragState.current.isDown || !listReference.current) return;
		const deltaX = event.clientX - dragState.current.startX;
		listReference.current.scrollLeft = dragState.current.scrollLeft - deltaX;
	}, []);

	const handlePointerUp = useCallback((event: React.PointerEvent) => {
		dragState.current.isDown = false;
		listReference.current?.releasePointerCapture(event.pointerId);
	}, []);

	const hasHeaderActions = !!onCloseAll || !!actions;

	if (tabs.length === 0) {
		return (
			<div
				className={cn(
					`
						flex h-tabs items-center justify-between border-b border-border
						bg-bg-secondary px-4
					`,
					className,
				)}
			>
				<span className="text-sm text-text-secondary">No files open</span>
				{hasHeaderActions && <div className="flex h-full shrink-0 items-stretch">{actions}</div>}
			</div>
		);
	}

	return (
		<div className={cn('flex shrink-0', className)}>
			<Tabs.Root value={activeTab} onValueChange={(value) => onSelect(value)} className="min-w-0 flex-1">
				<Tabs.List
					activateOnFocus
					ref={listReference}
					onWheel={handleWheel}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					className="
						flex h-tabs items-end gap-0 overflow-x-auto border-b border-border
						bg-bg-secondary select-none
					"
					style={{ scrollbarWidth: 'none' }}
				>
					{tabs.map((tab) => (
						<FileTabItem
							key={tab.path}
							tab={tab}
							isActive={tab.path === activeTab}
							showDirectory={duplicates.has(tab.path)}
							participants={participants.filter((participant) => participant.file === tab.path)}
							onClose={() => onClose(tab.path)}
						/>
					))}
				</Tabs.List>
			</Tabs.Root>
			{hasHeaderActions && (
				<div
					className="
						flex h-tabs shrink-0 items-center gap-0.5 border-b border-border
						bg-bg-secondary px-1
					"
				>
					{actions}
					{onCloseAll && tabs.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger>
								<Button type="button" focusStyle="inset" variant="ghost" size="icon" className="size-7" aria-label="Tab actions">
									<MoreHorizontal className="size-3.5" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" sideOffset={4}>
								<DropdownMenuItem onSelect={onCloseAll}>
									<ListX className="size-3.5" />
									<span>Close all tabs</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			)}
		</div>
	);
}

interface FileTabItemProperties {
	tab: FileTab;
	isActive: boolean;
	showDirectory: boolean;
	participants: Participant[];
	onClose: () => void;
}

function FileTabItem({ tab, isActive, showDirectory, participants, onClose }: FileTabItemProperties) {
	const filename = getFilename(tab.path);
	const iconColor = getFileIconColor(tab.path);
	const parentDirectory = showDirectory ? getParentDirectory(tab.path) : '';
	const displayLabel = tab.label ?? filename;

	const handleClose = (event: React.SyntheticEvent) => {
		event.stopPropagation();
		onClose();
	};

	const handleCloseKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			onClose();
		}
	};

	const handleMiddleClick = (event: React.MouseEvent) => {
		if (event.button === 1) {
			event.preventDefault();
			onClose();
		}
	};

	const closeLabel = showDirectory ? `Close ${tab.path}` : `Close ${displayLabel}`;

	return (
		<Tabs.Tab
			data-local-focus="true"
			value={tab.path}
			onMouseDown={handleMiddleClick}
			className={cn(
				`
					group relative flex h-9 cursor-pointer items-center gap-2 border-r
					border-border ps-3 pe-1 text-sm transition-colors
				`,
				`
					hover:bg-bg-tertiary
					focus-visible:ring-1 focus-visible:ring-accent focus-visible:outline-none
					focus-visible:ring-inset
				`,
				isActive
					? 'bg-bg-primary text-text-primary'
					: `
						bg-bg-secondary text-text-secondary
						hover:text-text-primary
					`,
			)}
		>
			{isActive && (
				<motion.span layoutId="file-tab-indicator" className="absolute inset-x-0 bottom-0 h-[2px] bg-accent" transition={springCritical} />
			)}
			<File className={cn('size-3.5 shrink-0', iconColor)} />
			<Tooltip content={tab.path}>
				<span className="max-w-utility-panel truncate">
					{tab.label ? displayLabel : filename}
					{!tab.label && parentDirectory && <span className="ml-1 text-text-secondary opacity-60">&#8249;{parentDirectory}&#8250;</span>}
				</span>
			</Tooltip>
			{participants.length > 0 && (
				<div className="flex shrink-0 items-center gap-0.5">
					{participants.slice(0, 3).map((participant) => (
						<span key={participant.id} className="size-1.5 rounded-full" style={{ backgroundColor: participant.color }} />
					))}
					{participants.length > 3 && <span className="text-3xs text-text-secondary">+{participants.length - 3}</span>}
				</div>
			)}
			{tab.isSaving ? (
				<Tooltip content="Saving…">
					<Spinner size="xs" className="shrink-0 text-accent" />
				</Tooltip>
			) : (
				tab.hasUnsavedChanges && (
					<Tooltip content="Unsaved changes">
						<span className="size-2 shrink-0 rounded-full bg-accent" />
					</Tooltip>
				)
			)}
			<Tooltip content="Close">
				<span
					role="button"
					data-local-focus="true"
					tabIndex={0}
					aria-label={closeLabel}
					onPointerDown={handleClosePointerDown}
					onClick={handleClose}
					onKeyDown={handleCloseKeyDown}
					className={cn(
						`
							flex size-4 shrink-0 cursor-pointer items-center justify-center
							rounded-sm transition-colors
						`,
						`
							opacity-0
							focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-accent
							focus-visible:outline-none focus-visible:ring-inset
							hover-always:bg-bg-secondary
							group-hover-always:opacity-100
						`,
						isActive && 'opacity-100',
					)}
				>
					<X className="size-3" />
				</span>
			</Tooltip>
		</Tabs.Tab>
	);
}
